import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Clock,
  CloudRain,
  Download,
  MoreHorizontal,
  Printer,
  Save,
  Trash2,
} from 'lucide-react';
import {
  capitalizeFirstWord,
  formatTimelineSpan,
  orderedTimelineSections,
  timelineEntryCount,
  type Timeline,
} from '@peles/shared';
import { DropdownMenu, type DropdownMenuItem } from '../common/DropdownMenu';
import { SaveAsTemplateModal } from '../event-ops/SaveAsTemplateButton';
import { RecordConversations } from '../messaging/RecordConversations';
import type { ThreadCount } from '../../hooks/useThreadCounts';

interface TimelineCardProps {
  timeline: Timeline;
  /** Where the card opens the editor. */
  href: string;
  onPrint?: (timeline: Timeline) => void;
  onExport: (timeline: Timeline) => void;
  onDelete: (timeline: Timeline) => void;
  /**
   * The timeline's conversation icon (COMMUNICATIONS-DESIGN H3): its count,
   * fetched once for the page, and the refetch to run when a conversation is
   * started or read. Absent — a template, or a reader outside the office — the
   * card shows none.
   */
  conversation?: { count: ThreadCount | null; onActivity: () => void };
}

const MAX_SECTION_CHIPS = 5;

/**
 * A timeline at a glance — its name, how much is in it, the hours it covers
 * and its sections — opening the editor, with the rest under "More".
 */
export function TimelineCard({
  timeline,
  href,
  onPrint,
  onExport,
  onDelete,
  conversation,
}: TimelineCardProps) {
  const [savingTemplate, setSavingTemplate] = useState(false);
  const sections = orderedTimelineSections(timeline.sections);
  const entries = sections.flatMap((s) => s.entries);
  const entryCount = timelineEntryCount(sections);
  const span = formatTimelineSpan(entries);
  const name = capitalizeFirstWord(timeline.title || 'Untitled timeline');

  const items: DropdownMenuItem[] = [
    ...(onPrint
      ? [
          {
            id: 'print',
            label: 'Print',
            icon: <Printer size={15} />,
            onSelect: () => onPrint(timeline),
          },
        ]
      : []),
    {
      id: 'export',
      label: 'Export as JSON',
      icon: <Download size={15} />,
      onSelect: () => onExport(timeline),
    },
    ...(!timeline.isTemplate
      ? [
          {
            id: 'template',
            label: 'Save as template',
            icon: <Save size={15} />,
            onSelect: () => setSavingTemplate(true),
          },
        ]
      : []),
    {
      id: 'delete',
      label: <span className="text-red-600">Delete</span>,
      icon: <Trash2 size={15} className="text-red-600" />,
      onSelect: () => onDelete(timeline),
      dividerBefore: true,
    },
  ];

  return (
    <article
      className="group relative bg-white rounded-xl border border-gray-200 shadow-sm hover:border-primary-300 hover:shadow transition"
      data-testid={`timeline-${timeline.id}`}
    >
      <div className="flex items-start gap-3 p-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <Clock size={20} aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-gray-900">
            {/* The whole card opens the editor; the menu sits above the link. */}
            <Link
              to={href}
              className="after:absolute after:inset-0 after:rounded-xl focus:outline-none focus-visible:underline"
              data-testid={`open-timeline-${timeline.id}`}
            >
              {name}
            </Link>
          </h2>
          <p className="mt-0.5 text-sm text-gray-500" data-testid={`timeline-meta-${timeline.id}`}>
            {sections.length === 1 ? '1 section' : `${sections.length} sections`} ·{' '}
            {entryCount === 1 ? '1 entry' : `${entryCount} entries`}
            {span && ` · ${span}`}
          </p>
          {sections.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Sections">
              {sections.slice(0, MAX_SECTION_CHIPS).map((s) => (
                <li
                  key={s.id}
                  className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                >
                  {capitalizeFirstWord(s.name)}
                  <span className="text-gray-400"> · {s.entries.length}</span>
                </li>
              ))}
              {sections.length > MAX_SECTION_CHIPS && (
                <li className="px-1 py-0.5 text-xs text-gray-400">
                  +{sections.length - MAX_SECTION_CHIPS} more
                </li>
              )}
            </ul>
          )}
          {timeline.rainPlanNotes && (
            <p className="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <CloudRain size={12} aria-hidden /> Rain plan written
            </p>
          )}
        </div>
        <div className="relative z-10 flex items-center gap-1 shrink-0">
          {conversation && !timeline.isTemplate && (
            <RecordConversations
              entityType="Timeline"
              entityId={timeline.id}
              variant="icon"
              count={conversation.count}
              onActivity={conversation.onActivity}
            />
          )}
          <DropdownMenu
            align="end"
            ariaLabel={`More for ${name}`}
            items={items}
            trigger={(props) => (
              <button
                type="button"
                {...props}
                className="flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 sm:p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label={`More for ${name}`}
                data-testid={`timeline-more-${timeline.id}`}
              >
                <MoreHorizontal size={18} />
              </button>
            )}
          />
          <ChevronRight
            size={18}
            className="text-gray-300 group-hover:text-primary-500"
            aria-hidden
          />
        </div>
      </div>
      <SaveAsTemplateModal
        open={savingTemplate}
        onClose={() => setSavingTemplate(false)}
        id={timeline.id}
        type="timeline"
        defaultName={`${timeline.title || 'Timeline'} Template`}
      />
    </article>
  );
}
